<template>
  <section class="section-container" id="contact">
    <div class="section-container-header-box">
      <p class="list-text">Contact</p>
    </div>
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>

    <form class="contact-form-box-container fade-up-animation" @submit.prevent="sendMail" novalidate>
      <div
        class="input-box"
        :class="{ 'input-box-text-has-value': contactData.name.length, 'is-invalid': validation.name.$errors[0] }">
        <div class="input-box-text">
          <p class="input-box-text-placeholder large-text-list">Your Name</p>
          <p class="input-box-text-placeholder large-text-list">|</p>
          <p class="input-box-text-value large-text-list">Elon Musk</p>
        </div>
        <input
          type="text"
          id="name"
          name="name"
          autocomplete="name"
          placeholder="Your Name"
          :disabled="submitted"
          v-model="contactData.name" />
      </div>

      <div
        class="input-box"
        :class="{ 'input-box-text-has-value': contactData.email.length, 'is-invalid': validation.email.$errors[0] }">
        <div class="input-box-text">
          <p class="input-box-text-placeholder large-text-list">Your Email</p>
          <p class="input-box-text-placeholder large-text-list">|</p>
          <p class="input-box-text-value large-text-list">elon@x.com</p>
        </div>
        <input
          type="email"
          inputmode="email"
          id="email"
          name="email"
          autocomplete="email"
          placeholder="Your Email"
          :disabled="submitted"
          v-model="contactData.email" />
      </div>

      <div class="contact-form-box-container-text">
        <p class="medium-text disabled-text legal-text text-center">
          By submitting this form, you agree to our
          <nuxt-link to="/privacy-policy" class="medium-text-bold disabled-text">
            <span class="text-animation">
              <p class="text-animation-visible medium-text-bold disabled-text">Privacy Policy</p>
              <p class="text-animation-hidden medium-text-bold disabled-text">Privacy Policy</p>
            </span>
          </nuxt-link>
          and
          <nuxt-link to="/terms-and-conditions" class="medium-text-bold disabled-text">
            <span class="text-animation">
              <p class="text-animation-visible medium-text-bold disabled-text">Terms and Conditions</p>
              <p class="text-animation-hidden medium-text-bold disabled-text">Terms and Conditions</p>
            </span> </nuxt-link
          >.
        </p>

        <p class="medium-text disabled-text legal-text text-center">
          This site is protected by reCAPTCHA and the Google
          <nuxt-link
            to="https://policies.google.com/privacy"
            external
            target="_blank"
            rel="noopener"
            class="medium-text-bold disabled-text">
            <span class="text-animation">
              <p class="text-animation-visible medium-text-bold disabled-text">Privacy Policy</p>
              <p class="text-animation-hidden medium-text-bold disabled-text">Privacy Policy</p>
            </span>
          </nuxt-link>
          and
          <nuxt-link
            to="https://policies.google.com/terms"
            external
            target="_blank"
            rel="noopener"
            class="medium-text-bold disabled-text">
            <span class="text-animation">
              <p class="text-animation-visible medium-text-bold disabled-text">Terms of Service</p>
              <p class="text-animation-hidden medium-text-bold disabled-text">Terms of Service</p>
            </span> </nuxt-link
          >.
        </p>

        <button
          type="submit"
          class="arrow-button arrow-button-bigger"
          :disabled="submitted"
          :class="{
            'arrow-button-loading': submitted,
          }">
          <p class="heading-4-bold">{{ submitted ? 'Submitting...' : 'Submit' }}</p>
          <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
        </button>
      </div>
    </form>
  </section>
  <section class="section-container section-container-double-gap">
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in callLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>

    <nuxt-link
      class="arrow-button arrow-button-bigger fade-up-animation"
      to="https://calendly.com/vortec-io"
      aria-label="Schedule a call"
      target="_blank">
      <p class="heading-4-bold">Schedule a call</p>
      <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div
    ></nuxt-link>
  </section>
</template>

<script lang="ts" setup>
import { Toaster } from 'vortec-components-front';
import { useVuelidate } from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import type { ValidationRule } from '@vuelidate/core';
import ErrorBag from '@/helpers/ErrorBag';
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import { useRecaptcha } from '@/composables/useRecaptcha';
import icons from '~/icons';

const { executeRecaptcha } = useRecaptcha();
const recaptchaKey = useRuntimeConfig().public.recaptchaKey as string;

const lines = [
  { text: 'Build the next', special: false },
  { text: 'big thing.', special: true },
];

const callLines = [
  { text: 'Prefer to talk?', special: false },
  { text: "Let's talk.", special: true },
];

useFadeUpObserver();

const contactData = ref({
  name: '',
  email: '',
});
const submitted = ref(false);
const errors = reactive(new ErrorBag());

const validationRules = computed(() => {
  const localRules: Record<string, Record<string, ValidationRule | Record<string, ValidationRule>>> = {};

  localRules.name = {
    required: helpers.withMessage('Name is required', required),
  };

  localRules.email = {
    required: helpers.withMessage('Email is required', required),
    email: helpers.withMessage('Email is not valid', email),
  };

  return localRules;
});

const validation = useVuelidate(validationRules, contactData);

async function sendMail() {
  validation.value.$touch();
  if (validation.value.$invalid) {
    Toaster().add({
      title: 'Error',
      message: validation.value.$errors[0]?.$message.toString() || 'Please fill in all the required fields correctly',
      type: 'error',
    });
    nextTick(() => {
      const firstInvalid = document.querySelectorAll('.is-invalid');
      if (firstInvalid.length > 0) {
        window.scrollTo({
          top: (firstInvalid[0] as HTMLElement).getBoundingClientRect().top + window.scrollY - 80,
          behavior: 'smooth',
        });
      }
    });
    return;
  }

  if (submitted.value) {
    return;
  }

  submitted.value = true;

  const recaptchaToken = await executeRecaptcha('contact');

  $fetch('/api/mailer', {
    method: 'POST',
    body: JSON.stringify({ ...contactData.value, recaptchaToken }),
    headers: {
      resource: 'Vortec',
      privilege: 'Contact',
      'recaptcha-key': recaptchaKey,
    },
  })
    .then((res: any) => {
      Toaster().add({
        title: 'Success',
        message: res.message,
        type: 'success',
      });
      validation.value.$reset();
      contactData.value = {
        name: '',
        email: '',
      };
    })
    .catch((error) => {
      Toaster().add({
        title: 'Error',
        message: Object.values(error.data.errors)[0]?.toString() || 'Something went wrong',
        type: 'error',
      });
      nextTick(() => {
        const firstInvalid = document.querySelectorAll('.is-invalid');
        if (firstInvalid.length > 0) {
          window.scrollTo({
            top: (firstInvalid[0] as HTMLElement).getBoundingClientRect().top + window.scrollY - 80,
            behavior: 'smooth',
          });
        }
      });
      if (error.response && error.response.data && error.response.data.errors) {
        errors.setAll(error.response.data.errors);
      }
    })
    .finally(() => {
      submitted.value = false;
    });
}
</script>
